/**
 * Toast — Notification system with success, error, info, warning types.
 * Uses a context provider + hook pattern.
 */

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { clsx } from 'clsx';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType, duration?: number) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const toastConfig: Record<ToastType, { icon: ReactNode; classes: string }> = {
  success: {
    icon: <CheckCircle2 size={20} />,
    classes: 'bg-success-50 border-success-500 text-success-700',
  },
  error: {
    icon: <AlertCircle size={20} />,
    classes: 'bg-danger-50 border-danger-500 text-danger-700',
  },
  info: {
    icon: <Info size={20} />,
    classes: 'bg-blue-50 border-blue-500 text-blue-700',
  },
  warning: {
    icon: <AlertTriangle size={20} />,
    classes: 'bg-warning-50 border-warning-500 text-warning-700',
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counterRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, type: ToastType = 'info', duration: number = 5000) => {
      const id = `toast-${++counterRef.current}`;
      setToasts((prev) => [...prev, { id, type, message, duration }]);

      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
    },
    [dismiss],
  );

  const value: ToastContextValue = {
    toast,
    success: (msg, dur) => toast(msg, 'success', dur),
    error: (msg, dur) => toast(msg, 'error', dur ?? 7000),
    info: (msg, dur) => toast(msg, 'info', dur),
    warning: (msg, dur) => toast(msg, 'warning', dur),
    dismiss,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast container */}
      <div
        className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-full pointer-events-none safe-top"
        aria-live="polite"
        aria-atomic="true"
      >
        {toasts.map((t) => {
          const cfg = toastConfig[t.type];
          return (
            <div
              key={t.id}
              className={clsx(
                'flex items-start gap-3 p-4 rounded-lg border-l-4 shadow-md pointer-events-auto',
                'animate-in slide-in-from-right fade-in duration-300',
                cfg.classes,
              )}
              role="alert"
            >
              <span className="flex-shrink-0">{cfg.icon}</span>
              <p className="flex-1 text-sm font-medium leading-snug">{t.message}</p>
              <button
                onClick={() => dismiss(t.id)}
                className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
                aria-label="Dismiss notification"
              >
                <X size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}

export default ToastProvider;